import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { CancelServiceOrderPayload } from 'contracts/service-orders';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { TextareaGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components/activityIndicators';

import { formSchema, type FormSchema } from './form-schema';

type Props = {
  loading?: boolean
  onSubmit: (data: CancelServiceOrderPayload) => void
}

type Ref = {
  open: (orderId: number) => void
  close: () => void
}

export type CancelOrderDrawerRef = Ref;

export const CancelOrderDrawer: React.ForwardRefExoticComponent<Props & React.RefAttributes<Ref>> = React.forwardRef<Ref, Props>(({
                                                                                                                                    loading,
                                                                                                                                    onSubmit,
                                                                                                                                  }, ref) => {

  const [open, setOpen] = React.useState(false);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      cancellationReason: '',
    },
  });

  const handleSubmit = React.useCallback((data: FormSchema) => {
    onSubmit({
      serviceOrderId: data.serviceOrderId,
      data: {
        cancellationReason: data.cancellationReason,
      },
    });
  }, [onSubmit]);

  const onOpenChange = React.useCallback((
    open: boolean,
    orderId?: number,
  ) => {
    setOpen(open);
    form.reset();
    orderId && form.setValue('serviceOrderId', orderId);
  }, [form]);

  React.useImperativeHandle(ref, () => {
    return {
      open: (orderId) => {
        onOpenChange(true, orderId);
      },
      close: () => {
        onOpenChange(false);
      },
    };
  }, [onOpenChange]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Content>
        <div className="max-w-[800px] w-full mx-auto">
          <Drawer.Header>
            <Drawer.Title className="text-center flex flex-col font-GilroyBold text-gray-600 text-[16px]">
              <span>Cancelar Ordem de Serviço</span>
            </Drawer.Title>
          </Drawer.Header>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="flex flex-col gap-4 pb-10"
            >
              <FormField
                control={form.control}
                name={'cancellationReason'}
                render={({ field }) => (
                  <TextareaGs
                    {...field}
                    label={'Justificativa'}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <div className={'grid grid-cols-2 gap-4'}>
                <Button.Root
                  type={'button'}
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => onOpenChange(false)}
                >
                  Fechar
                </Button.Root>
                <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                  {loading ? <ActivityIndicator /> : 'Confirmar cancelamento'}
                </Button.Root>
              </div>
            </form>
          </Form>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
});